import { FC, useEffect, useState, useRef } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "../lib/toast";
import { apiClient } from "../utils/api";
import ConnectionStatus from "../components/ConnectionStatus";

const Landing: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasError, setHasError] = useState(false);
  const hasFetchedData = useRef(false);

  // Show error from OAuth callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      toast({
        title: "Authorization Failed",
        description: error,
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch auth URL on component mount
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      // Prevent duplicate API calls
      if (hasFetchedData.current) {
        return;
      }
      hasFetchedData.current = true;

      try {
        const response = await apiClient.getHubSpotAuthUrl();

        if (!isMounted) return;

        if (response.success) {
          setAuthUrl(response.authUrl);
          setHasError(false);
        } else {
          setHasError(true);
          toast({
            title: "Error",
            description:
              response.error || "Failed to get HubSpot authorization URL",
            variant: "destructive",
          });
        }
      } catch (error) {
        if (!isMounted) return;

        setHasError(true);
        // Only show toast for non-connection errors to avoid spam
        const connectionState = apiClient.getConnectionState();
        if (!connectionState.isOnline) {
        } else {
          toast({
            title: "Error",
            description: "Failed to connect to the server. Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      // Cancel any pending requests when component unmounts
      apiClient.cancelAllRequests();
    };
  }, []);

  const handleHubSpotAuth = () => {
    if (authUrl) {
      setIsLoading(true);
      // Redirect to HubSpot OAuth URL
      window.location.href = authUrl;
    } else {
      toast({
        title: "Error",
        description:
          "Authorization URL not available. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  const handleRetry = async () => {
    setHasError(false);
    setIsInitializing(true);

    try {
      const response = await apiClient.getHubSpotAuthUrl();

      if (response.success) {
        setAuthUrl(response.authUrl);
        setHasError(false);
      } else {
        setHasError(true);
        toast({
          title: "Error",
          description:
            response.error || "Failed to get HubSpot authorization URL",
          variant: "destructive",
        });
      }
    } catch (error) {
      setHasError(true);
      toast({
        title: "Error",
        description: "Failed to connect to the server. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <Card className="w-full max-w-[38rem] shadow-2xl border-0">
        <CardHeader className="text-center pb-4">
          <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
            HubSpot-Veracross Integration
          </CardTitle>
          <CardDescription className="text-gray-600">
            Connect your educational management system with HubSpot CRM for
            seamless data synchronization.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ConnectionStatus />
          {/* Brief Description */}
          <p className="text-gray-700 text-sm text-center leading-relaxed">
            Streamline your admission process by automatically syncing data
            between Veracross and HubSpot. Map custom fields, track
            applications, and maintain consistent data across both platforms.
          </p>

          {/* Key Benefits */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">
              Key Features
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs max-w-md mx-auto justify-items-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                  ✓
                </div>
                <span className="text-gray-700">Secure OAuth</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                  ✓
                </div>
                <span className="text-gray-700">Custom Mapping</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                  ✓
                </div>
                <span className="text-gray-700">Real-time Sync</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                  ✓
                </div>
                <span className="text-gray-700">Auto Workflows</span>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center">
            {hasError ? (
              <div className="flex flex-col items-center space-y-3">
                <p className="text-sm text-red-600 text-center">
                  Failed to load authorization URL. Please try again.
                </p>
                <Button
                  onClick={handleRetry}
                  disabled={isInitializing}
                  size="lg"
                  className="px-8 py-3 text-base font-semibold bg-gray-600 hover:bg-gray-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 w-[200px]"
                >
                  {isInitializing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Retrying...
                    </>
                  ) : (
                    "Retry"
                  )}
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleHubSpotAuth}
                disabled={isLoading || isInitializing || !authUrl}
                size="lg"
                className="px-8 py-3 text-base font-semibold bg-[#ff7a59] hover:bg-[#e66a4a] text-white shadow-lg hover:shadow-xl transition-all duration-200 w-[200px]"
              >
                {isInitializing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Authorizing...
                  </>
                ) : (
                  "Authorize HubSpot"
                )}
              </Button>
            )}
          </div>

          {/* Agreement Text */}
          <div className="text-center text-xs text-gray-600 pt-2">
            <p>
              By continuing, you agree with Niswey's{" "}
              <a
                href="https://www.niswey.com/data-processing-agreement"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ff7a59] hover:text-[#e66a4a] underline"
              >
                Data processing Agreement
              </a>{" "}
              and{" "}
              <a
                href="https://www.niswey.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ff7a59] hover:text-[#e66a4a] underline"
              >
                Privacy Policy
              </a>
            </p>
          </div>

          {/* Footer Note */}
          <div className="text-center text-xs text-gray-500 pt-4 border-t border-gray-100">
            <p>
              You'll be redirected to HubSpot's authorization page to securely
              grant access to your account.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Landing;
